<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const getters = useStoreGetters();
const accountId = computed(() => getters.getCurrentAccountId.value);

const dispatchUrl = computed(() => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}/dispatch`;
});

const shortDispatchUrl = computed(() => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/d/${accountId.value}/${props.inbox.id}`;
});

const paramRows = computed(() => [
  {
    name: 'phone_number',
    type: 'string',
    required: true,
    desc: t('INBOX_MGMT.DISPATCH.PARAM_PHONE'),
  },
  {
    name: 'message',
    type: 'string',
    required: 'conditional',
    desc: t('INBOX_MGMT.DISPATCH.PARAM_MESSAGE'),
  },
  {
    name: 'template_name',
    type: 'string',
    required: 'conditional',
    desc: t('INBOX_MGMT.DISPATCH.PARAM_TEMPLATE_NAME'),
  },
  {
    name: 'template_language',
    type: 'string',
    required: false,
    desc: t('INBOX_MGMT.DISPATCH.PARAM_TEMPLATE_LANG'),
  },
  {
    name: 'template_params',
    type: 'object',
    required: false,
    desc: t('INBOX_MGMT.DISPATCH.PARAM_TEMPLATE_PARAMS'),
  },
  {
    name: 'name',
    type: 'string',
    required: false,
    desc: t('INBOX_MGMT.DISPATCH.PARAM_NAME'),
  },
  {
    name: 'email',
    type: 'string',
    required: false,
    desc: t('INBOX_MGMT.DISPATCH.PARAM_EMAIL'),
  },
  {
    name: 'identifier',
    type: 'string',
    required: false,
    desc: t('INBOX_MGMT.DISPATCH.PARAM_IDENTIFIER'),
  },
  {
    name: 'custom_attributes',
    type: 'object',
    required: false,
    desc: t('INBOX_MGMT.DISPATCH.PARAM_CUSTOM_ATTRS'),
  },
]);

const erpAliasRows = computed(() => [
  {
    standard: 'phone_number',
    aliases: 'dest, number',
    desc: t('INBOX_MGMT.DISPATCH.ERP_ALIAS_PHONE'),
  },
  {
    standard: 'message',
    aliases: 'text',
    desc: t('INBOX_MGMT.DISPATCH.ERP_ALIAS_MESSAGE'),
  },
  {
    standard: t('INBOX_MGMT.DISPATCH.ERP_AUTH_LABEL'),
    aliases: 'user, pw',
    desc: t('INBOX_MGMT.DISPATCH.ERP_ALIAS_AUTH'),
  },
]);

const curlErpExample = computed(() => {
  return `curl "${shortDispatchUrl.value}?api_access_token=SEU_TOKEN&dest=11999999999&text=Ol%C3%A1+Jo%C3%A3o%2C+sua+fatura+vence+amanh%C3%A3.&pw=Jo%C3%A3o+Silva"`;
});

const curlTextExample = computed(() => {
  return [
    `curl -X POST ${dispatchUrl.value} \\`,
    '  -H "Content-Type: application/json" \\',
    '  -H "Authorization: Bearer SEU_TOKEN" \\',
    "  -d '{",
    '    "phone_number": "11999999999",',
    '    "name": "João Silva",',
    '    "message": "Olá João, sua fatura vence amanhã."',
    "  }'",
  ].join('\n');
});

const curlTemplateExample = computed(() => {
  return [
    `curl -X POST ${dispatchUrl.value} \\`,
    '  -H "Content-Type: application/json" \\',
    '  -H "Authorization: Bearer SEU_TOKEN" \\',
    "  -d '{",
    '    "phone_number": "11999999999",',
    '    "template_name": "billing_reminder",',
    '    "template_language": "pt_BR",',
    '    "template_params": {',
    '      "body": { "1": "João", "2": "R$150" }',
    '    }',
    "  }'",
  ].join('\n');
});

const responseExample = [
  '{',
  '  "contact_id": 123,',
  '  "conversation_id": 456,',
  '  "message_id": 789,',
  '  "phone_number": "+5511999999999"',
  '}',
].join('\n');

function copyToClipboard(text) {
  navigator.clipboard.writeText(text);
  useAlert(t('INBOX_MGMT.DISPATCH.COPIED'));
}

function requiredBadge(required) {
  if (required === true) return 'required';
  if (required === 'conditional') return 'conditional';
  return '';
}
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div class="flex flex-col gap-6 p-6 bg-n-solid-1">
    <!-- Header -->
    <div class="flex gap-3 items-center">
      <div
        class="flex justify-center items-center w-10 h-10 rounded-lg bg-n-blue-2"
      >
        <Icon icon="i-lucide-send" class="w-5 h-5 text-n-blue-11" />
      </div>
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.DISPATCH.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.DISPATCH.DESCRIPTION') }}
        </p>
      </div>
    </div>

    <!-- Endpoint URL Card -->
    <div
      class="flex flex-col gap-4 p-5 rounded-lg border border-n-weak bg-n-solid-2"
    >
      <div class="flex gap-2 items-center">
        <Icon icon="i-lucide-link" class="w-4 h-4 text-n-slate-11" />
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.DISPATCH.ENDPOINT_LABEL') }}
        </h3>
      </div>
      <div
        class="flex gap-2 items-center p-3 rounded-lg bg-n-solid-1 border border-n-weak"
      >
        <code class="text-xs font-mono text-n-slate-11 break-all flex-1">
          POST / GET {{ dispatchUrl }}
        </code>
        <button
          class="flex-shrink-0 p-1.5 text-n-slate-10 hover:text-n-slate-12 rounded hover:bg-n-slate-3 transition-colors"
          @click="copyToClipboard(dispatchUrl)"
        >
          <Icon icon="i-lucide-copy" class="w-4 h-4" />
        </button>
      </div>
    </div>

    <!-- Authentication Card -->
    <div
      class="flex flex-col gap-4 p-5 rounded-lg border border-n-weak bg-n-solid-2"
    >
      <div class="flex gap-2 items-center">
        <Icon icon="i-lucide-key" class="w-4 h-4 text-n-slate-11" />
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.DISPATCH.AUTH_LABEL') }}
        </h3>
      </div>
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.DISPATCH.AUTH_HELP') }}
      </p>
      <div
        class="flex gap-2 items-center p-3 rounded-lg bg-n-solid-1 border border-n-weak"
      >
        <Icon
          icon="i-lucide-shield-check"
          class="w-4 h-4 text-n-slate-10 flex-shrink-0"
        />
        <code class="text-xs font-mono text-n-slate-11">
          Authorization: Bearer SEU_TOKEN
        </code>
      </div>
    </div>

    <!-- Parameters Card -->
    <div
      class="flex flex-col gap-4 p-5 rounded-lg border border-n-weak bg-n-solid-2"
    >
      <div class="flex gap-2 items-center">
        <Icon icon="i-lucide-list" class="w-4 h-4 text-n-slate-11" />
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.DISPATCH.PARAMS_LABEL') }}
        </h3>
      </div>

      <div class="grid grid-cols-1 gap-2">
        <div
          v-for="param in paramRows"
          :key="param.name"
          class="flex gap-3 items-start p-3 rounded-lg border transition-all"
          :class="[
            param.required === true
              ? 'border-n-blue-6 bg-n-blue-2'
              : param.required === 'conditional'
                ? 'border-n-amber-6 bg-n-amber-2'
                : 'border-n-weak bg-n-solid-1',
          ]"
        >
          <div class="flex-1 min-w-0">
            <div class="flex gap-2 items-center flex-wrap">
              <code
                class="text-xs font-mono font-medium px-1.5 py-0.5 rounded bg-n-slate-3 text-n-slate-12"
              >
                {{ param.name }}
              </code>
              <span class="text-xs text-n-slate-10">{{ param.type }}</span>
              <span
                v-if="requiredBadge(param.required) === 'required'"
                class="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-n-blue-3 text-n-blue-11 border border-n-blue-6"
              >
                {{ $t('INBOX_MGMT.DISPATCH.BADGE_REQUIRED') }}
              </span>
              <span
                v-if="requiredBadge(param.required) === 'conditional'"
                class="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-n-amber-3 text-n-amber-11 border border-n-amber-6"
              >
                {{ $t('INBOX_MGMT.DISPATCH.BADGE_CONDITIONAL') }}
              </span>
            </div>
            <p class="text-xs text-n-slate-11 mt-1">
              {{ param.desc }}
            </p>
          </div>
        </div>
      </div>

      <div
        class="flex gap-2 items-start p-3 rounded-lg bg-n-blue-2 border border-n-blue-6"
      >
        <Icon
          icon="i-lucide-info"
          class="flex-shrink-0 w-4 h-4 text-n-blue-11 mt-0.5"
        />
        <p class="text-xs text-n-blue-11">
          {{ $t('INBOX_MGMT.DISPATCH.REQUIRED_NOTE') }}
        </p>
      </div>
    </div>

    <!-- Text Example Card -->
    <div
      class="flex flex-col gap-4 p-5 rounded-lg border border-n-weak bg-n-solid-2"
    >
      <div class="flex gap-2 items-center justify-between">
        <div class="flex gap-2 items-center">
          <Icon
            icon="i-lucide-message-square"
            class="w-4 h-4 text-n-slate-11"
          />
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.DISPATCH.EXAMPLE_TEXT_LABEL') }}
          </h3>
        </div>
        <button
          class="flex gap-1 items-center px-2 py-1 text-xs text-n-slate-11 hover:text-n-slate-12 rounded hover:bg-n-slate-3 transition-colors"
          @click="copyToClipboard(curlTextExample)"
        >
          <Icon icon="i-lucide-copy" class="w-3.5 h-3.5" />
          {{ $t('INBOX_MGMT.DISPATCH.COPY') }}
        </button>
      </div>
      <!-- eslint-disable vue/html-closing-bracket-newline -->
      <!-- eslint-disable prettier/prettier -->
      <pre class="p-4 text-xs font-mono bg-n-solid-1 text-n-slate-12 rounded-lg border border-n-weak overflow-x-auto leading-relaxed">{{ curlTextExample }}</pre>
    </div>

    <!-- Template Example Card -->
    <div
      class="flex flex-col gap-4 p-5 rounded-lg border border-n-weak bg-n-solid-2"
    >
      <div class="flex gap-2 items-center justify-between">
        <div class="flex gap-2 items-center">
          <Icon icon="i-lucide-file-text" class="w-4 h-4 text-n-slate-11" />
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('INBOX_MGMT.DISPATCH.EXAMPLE_TEMPLATE_LABEL') }}
          </h3>
        </div>
        <button
          class="flex gap-1 items-center px-2 py-1 text-xs text-n-slate-11 hover:text-n-slate-12 rounded hover:bg-n-slate-3 transition-colors"
          @click="copyToClipboard(curlTemplateExample)"
        >
          <Icon icon="i-lucide-copy" class="w-3.5 h-3.5" />
          {{ $t('INBOX_MGMT.DISPATCH.COPY') }}
        </button>
      </div>
      <!-- eslint-disable vue/html-closing-bracket-newline -->
      <!-- eslint-disable prettier/prettier -->
      <pre class="p-4 text-xs font-mono bg-n-solid-1 text-n-slate-12 rounded-lg border border-n-weak overflow-x-auto leading-relaxed">{{ curlTemplateExample }}</pre>
    </div>

    <!-- ERP Compatibility Card -->
    <div
      class="flex flex-col gap-4 p-5 rounded-lg border border-n-amber-6 bg-n-amber-2"
    >
      <div class="flex gap-2 items-center">
        <Icon icon="i-lucide-plug" class="w-4 h-4 text-n-amber-11" />
        <h3 class="text-sm font-semibold text-n-amber-12">
          {{ $t('INBOX_MGMT.DISPATCH.ERP_TITLE') }}
        </h3>
      </div>
      <p class="text-sm text-n-amber-11">
        {{ $t('INBOX_MGMT.DISPATCH.ERP_DESCRIPTION') }}
      </p>

      <div class="flex gap-2 items-center">
        <Icon icon="i-lucide-link" class="w-4 h-4 text-n-amber-11" />
        <h4 class="text-xs font-semibold text-n-amber-12">
          {{ $t('INBOX_MGMT.DISPATCH.ERP_SHORT_URL_LABEL') }}
        </h4>
      </div>
      <div
        class="flex gap-2 items-center p-3 rounded-lg bg-n-solid-1 border border-n-amber-6"
      >
        <code class="text-xs font-mono text-n-amber-12 break-all flex-1">
          {{ shortDispatchUrl }}
        </code>
        <button
          class="flex-shrink-0 p-1.5 text-n-amber-10 hover:text-n-amber-12 rounded hover:bg-n-amber-3 transition-colors"
          @click="copyToClipboard(shortDispatchUrl)"
        >
          <Icon icon="i-lucide-copy" class="w-4 h-4" />
        </button>
      </div>

      <div class="grid grid-cols-1 gap-2">
        <div
          v-for="alias in erpAliasRows"
          :key="alias.standard"
          class="flex gap-3 items-start p-3 rounded-lg border border-n-amber-6 bg-n-solid-1"
        >
          <div class="flex-1 min-w-0">
            <div class="flex gap-2 items-center flex-wrap">
              <code
                class="text-xs font-mono font-medium px-1.5 py-0.5 rounded bg-n-amber-3 text-n-amber-12"
              >
                {{ alias.aliases }}
              </code>
              <Icon icon="i-lucide-arrow-right" class="w-3 h-3 text-n-slate-10" />
              <code
                class="text-xs font-mono font-medium px-1.5 py-0.5 rounded bg-n-slate-3 text-n-slate-12"
              >
                {{ alias.standard }}
              </code>
            </div>
            <p class="text-xs text-n-slate-11 mt-1">
              {{ alias.desc }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex gap-2 items-center justify-between">
        <div class="flex gap-2 items-center">
          <Icon icon="i-lucide-terminal" class="w-4 h-4 text-n-amber-11" />
          <h4 class="text-xs font-semibold text-n-amber-12">
            {{ $t('INBOX_MGMT.DISPATCH.ERP_EXAMPLE_LABEL') }}
          </h4>
        </div>
        <button
          class="flex gap-1 items-center px-2 py-1 text-xs text-n-amber-11 hover:text-n-amber-12 rounded hover:bg-n-amber-3 transition-colors"
          @click="copyToClipboard(curlErpExample)"
        >
          <Icon icon="i-lucide-copy" class="w-3.5 h-3.5" />
          {{ $t('INBOX_MGMT.DISPATCH.COPY') }}
        </button>
      </div>
      <!-- eslint-disable vue/html-closing-bracket-newline -->
      <!-- eslint-disable prettier/prettier -->
      <pre class="p-4 text-xs font-mono bg-n-solid-1 text-n-slate-12 rounded-lg border border-n-amber-6 overflow-x-auto leading-relaxed whitespace-pre-wrap break-all">{{ curlErpExample }}</pre>
    </div>

    <!-- Response Example Card -->
    <div
      class="flex flex-col gap-4 p-5 rounded-lg border border-n-weak bg-n-solid-2"
    >
      <div class="flex gap-2 items-center">
        <Icon icon="i-lucide-check-circle" class="w-4 h-4 text-n-green-11" />
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.DISPATCH.RESPONSE_LABEL') }}
        </h3>
      </div>
      <!-- eslint-disable vue/html-closing-bracket-newline -->
      <!-- eslint-disable prettier/prettier -->
      <pre class="p-4 text-xs font-mono bg-n-solid-1 text-n-slate-12 rounded-lg border border-n-weak overflow-x-auto leading-relaxed">{{ responseExample }}</pre>
    </div>

    <!-- Info Alert -->
    <div
      class="flex gap-3 p-4 rounded-lg border border-n-green-6 bg-n-green-2"
    >
      <Icon
        icon="i-lucide-zap"
        class="flex-shrink-0 w-5 h-5 text-n-green-11 mt-0.5"
      />
      <div class="flex-1">
        <div class="text-sm font-medium text-n-green-12">
          {{ $t('INBOX_MGMT.DISPATCH.INFO_TITLE') }}
        </div>
        <div class="text-sm text-n-green-11 mt-1">
          {{ $t('INBOX_MGMT.DISPATCH.INFO_MESSAGE') }}
        </div>
      </div>
    </div>
  </div>
</template>
